import { toRaw } from '@sveltia/utils/object';

import { suspendAutoDuplication } from '$lib/services/contents/draft';
import { createProxy, getListItemMatcher } from '$lib/services/contents/draft/create/proxy.svelte';
import { getDefaultValues } from '$lib/services/contents/draft/defaults';
import { getDraftFieldArgs, getField } from '$lib/services/contents/entry/fields';
import { isKeyPathWithin } from '$lib/services/contents/entry/key-paths';
import { TEXT_FIELD_TYPES } from '$lib/services/contents/fields';
import { hasUuidTag } from '$lib/services/contents/fields/compute/helpers';
import { getDuplicateKeysFieldKeyPaths } from '$lib/services/contents/fields/key-value/duplicate-keys';
import { getPairsFromContent, setPairs } from '$lib/services/contents/fields/key-value/pairs';
import { getLocalizedFieldValue } from '$lib/services/contents/fields/locale';
import { getInitialValue as getInitialUuidValue } from '$lib/services/contents/fields/uuid/helpers';
import { getDraftI18nConfig } from '$lib/services/contents/i18n/config';
import {
  isFieldI18nDisabled,
  isFieldLocalized,
  isFieldMirrored,
  isFieldTranslatable,
} from '$lib/services/contents/i18n/fields';
import { isDuplicatedField } from '$lib/services/contents/i18n/shared-values';

/**
 * @import { EntryDraft, FlattenedEntryContent, InternalLocaleCode } from '$lib/types/private';
 * @import { ComputeField, Field, FieldKeyPath, HiddenField, UuidField } from '$lib/types/public';
 */

/**
 * Run the given callback for every locale a field update has to be written to.
 *
 * A field with the `duplicate` or `sync` i18n strategy holds the same value in every locale, so an
 * update has to reach all of them, whichever locale it’s made in; any other field is written to the
 * given locale only.
 *
 * The draft proxy duplicates a `duplicate` field on its own whenever a value is assigned, which
 * would write every value twice here, so the callback runs with that suspended. Suspensions nest,
 * so a caller whose operation is wider than this loop can still suspend around the whole thing.
 *
 * A field nested in a List or Object field with the `duplicate` strategy is duplicated along with
 * it, even without the strategy of its own. Pass the draft and the key path to have that taken into
 * account, so that a list item or object added to such a field reaches every locale.
 * @param {object} args Arguments.
 * @param {Record<InternalLocaleCode, FlattenedEntryContent> | undefined} args.valueStore Value
 * store to update, e.g. `draft.currentValues`, keyed by locale.
 * @param {InternalLocaleCode} args.locale Locale being edited.
 * @param {Field['i18n']} args.i18n Field i18n configuration.
 * @param {EntryDraft} [args.draft] Entry draft the value store belongs to.
 * @param {FieldKeyPath} [args.keyPath] Key path of the field being updated.
 * @param {(valueMap: FlattenedEntryContent, locale: InternalLocaleCode) => void} callback Function
 * to run for each target locale, taking that locale’s content and the locale code.
 */
export const forEachTargetLocale = ({ valueStore, locale, i18n, draft, keyPath }, callback) => {
  // The field configuration can only be looked up for the entry’s own values, not for those of a
  // rich text editor component, which live in another value store
  const getFieldArgs =
    draft && keyPath && valueStore === draft.currentValues
      ? getDraftFieldArgs(draft, { keyPath, valueMap: valueStore[locale] })
      : undefined;

  // Resolve the field before any callback changes the values it’s resolved from. Look up the
  // configuration rather than relying on the given `i18n`, which the caller may have defaulted to
  // `false` for a field without the option, which would hide the ancestor’s strategy
  const fieldConfig = getFieldArgs ? getField(getFieldArgs) : undefined;

  const duplicated =
    isFieldMirrored(i18n) ||
    (!!getFieldArgs &&
      isDuplicatedField({
        fieldConfig: fieldConfig ?? /** @type {Field} */ ({ i18n }),
        getFieldArgs,
      }));

  // A field in an item of a translatable List field is only written to the locales that have the
  // same item
  const matchesListItems =
    duplicated && getFieldArgs ? getListItemMatcher({ fieldConfig, getFieldArgs }) : () => true;

  suspendAutoDuplication(() => {
    Object.entries(valueStore ?? {}).forEach(([_locale, valueMap]) => {
      if (_locale === locale || (duplicated && matchesListItems(valueMap))) {
        callback(valueMap, _locale);
      }
    });
  });
};

/**
 * Populate the given localized content with values from the default locale.
 * @param {object} args Arguments.
 * @param {EntryDraft} args.draft Entry draft.
 * @param {FlattenedEntryContent} args.content Original content for the current locale.
 * @param {InternalLocaleCode} args.targetLanguage Target locale.
 * @param {FieldKeyPath} [args.keyPathPrefix] Key path of the parent Object field being populated,
 * e.g. `blocks.0.image`. When specified, only the keys under that key path are returned, so
 * unrelated default locale fields — including list items that don’t exist in the target locale —
 * are not copied over. The whole default locale content is still used to look up field
 * configurations, which requires sibling keys such as a variable type key.
 * @returns {FlattenedEntryContent} Updated content.
 */
export const copyDefaultLocaleValues = ({ draft, content, targetLanguage, keyPathPrefix }) => {
  const { currentValues } = draft;
  const { defaultLocale } = getDraftI18nConfig(draft);
  /** @type {FlattenedEntryContent} */
  const defaultLocaleContent = toRaw(currentValues[defaultLocale]);
  const newContent = { ...toRaw(content), ...defaultLocaleContent };
  const getFieldArgs = getDraftFieldArgs(draft, { valueMap: newContent });
  /** @type {string[]} */
  const noI18nFieldKeys = [];

  // Process the merged content
  Object.keys(newContent).forEach((keyPath) => {
    const field = getField({ ...getFieldArgs, keyPath });

    if (!field) {
      return;
    }

    const { widget: fieldType = 'text' } = field;

    // A field without an `i18n` option of its own is duplicated along with a List or Object field
    // using the `duplicate` or `sync` strategy, so it has to be kept rather than removed as
    // non-i18n
    const i18n =
      field.i18n ??
      (isDuplicatedField({ fieldConfig: field, getFieldArgs: { ...getFieldArgs, keyPath } })
        ? 'duplicate'
        : false);

    // Reset the field value to the default value or an empty string if the field is a text-like
    // field type and i18n is enabled, because the content would likely be translated by the user.
    // Otherwise, the content would be copied from the default locale. A `duplicate_if_empty` field
    // keeps the default locale’s value until the user enters one of their own
    if (
      TEXT_FIELD_TYPES.includes(fieldType) &&
      isFieldTranslatable(i18n) &&
      i18n !== 'duplicate_if_empty'
    ) {
      newContent[keyPath] = content[keyPath] ?? '';
    }

    // A translatable UUID field gets a UUID of its own rather than the default locale’s, just like
    // each locale of a new draft does (see `fillUuidValues()`). A Compute field generating UUIDs is
    // cleared for the same reason, as it keeps the UUIDs found in its value when resolved again
    if (isFieldTranslatable(i18n)) {
      if (fieldType === 'uuid') {
        newContent[keyPath] =
          content[keyPath] || getInitialUuidValue(/** @type {UuidField} */ (field));
      }

      if (fieldType === 'compute' && hasUuidTag(/** @type {ComputeField} */ (field).value)) {
        newContent[keyPath] = '';
      }
    }

    // Support special case for the Hidden field with `default` value set to `{{locale}}`: if the
    // field value is `{{locale}}`, replace it with the target locale
    if (fieldType === 'hidden' && isFieldTranslatable(i18n)) {
      const { default: defaultValue } = /** @type {HiddenField} */ (field);

      if (defaultValue === '{{locale}}') {
        newContent[keyPath] = targetLanguage;
      }
    }

    // A value copied from the default locale may be a Relation field value holding that locale as a
    // prefix, or a DateTime field value written in its language with the `localize_format` option,
    // so localize it for the target locale
    if (keyPath in defaultLocaleContent) {
      newContent[keyPath] = getLocalizedFieldValue({
        fieldConfig: field,
        value: newContent[keyPath],
        sourceLocale: defaultLocale,
        targetLocale: targetLanguage,
      });
    }

    // Remove `null` values for object fields if i18n is enabled and the field is enabled in the
    // default locale, otherwise the subfields will not be saved in the current locale
    if (
      fieldType === 'object' &&
      isFieldLocalized(i18n) &&
      defaultLocaleContent[keyPath] !== null
    ) {
      delete newContent[keyPath];
    }

    // Remove the field if i18n is disabled
    if (isFieldI18nDisabled(i18n) || noI18nFieldKeys.some((key) => isKeyPathWithin(keyPath, key))) {
      delete newContent[keyPath];
      noI18nFieldKeys.push(keyPath);
    }
  });

  // A KeyValue field with the `duplicate_keys` strategy takes its keys from the default locale but
  // not its values, which are left for the user to fill in like a translatable text field. The
  // merged content holds the pairs of every such field that is still enabled in this locale
  getDuplicateKeysFieldKeyPaths({
    valueStore: { [targetLanguage]: newContent },
    getFieldArgs,
  }).forEach((keyPath) => {
    setPairs(
      newContent,
      keyPath,
      getPairsFromContent(defaultLocaleContent, keyPath).map(([key]) => [
        key,
        content[`${keyPath}.${key}`] ?? '',
      ]),
    );
  });

  if (keyPathPrefix !== undefined) {
    return Object.fromEntries(
      Object.entries(newContent).filter(([keyPath]) => keyPath.startsWith(`${keyPathPrefix}.`)),
    );
  }

  return newContent;
};

/**
 * Enable or disable the given locale’s content output for the entry draft.
 * @param {object} args Arguments.
 * @param {EntryDraft} args.draft Entry draft.
 * @param {InternalLocaleCode} args.locale Locale.
 */
export const toggleLocale = ({ draft, locale }) => {
  const { fields, defaultLocale, currentLocales, currentValues, validities, validationMessages } =
    draft;

  const enabled = !currentLocales[locale];

  currentLocales[locale] = enabled;

  // Initialize the content for the locale
  if (enabled && !currentValues[locale]) {
    const newContent = getDefaultValues({ fields, locale, defaultLocale });

    draft.originalValues[locale] = newContent;

    currentValues[locale] = createProxy({
      draft,
      locale,
      target: copyDefaultLocaleValues({ draft, content: newContent, targetLanguage: locale }),
    });

    return;
  }

  validities[locale] = {};
  validationMessages[locale] = {};
};
